// ===== KanbanBoard + LeadCard + LostReasonModal =====
import React, { useEffect, useRef } from 'react';
import { Icon } from './icons.jsx';
import { HeatDot, OriginPill } from './primitives.jsx';
import { useStore } from './store.jsx';
import { usePermissions, useEscreveNoLead } from './auth.jsx';
import { TipoChip } from './clientes.jsx';
import { ModalShell } from './modals.jsx';
import {
  STAGE_GATES, GateModal, gateStatus, leadHealth, phBaseLabel,
} from './pipeline-health.jsx';
import {
  AGENTS, LOST_REASONS, STAGE_NAMES,
  TIPO_CLIENTE_BY_ID, TODAY, brl, brlCompact,
} from './data.jsx';

const { useState: useStateKb, useMemo: useMemoKb } = React;

// Dias parado = desde o último movimento de estágio (fallback: dias no estágio)
function diasParado(lead) {
  if (lead.ultimoMovimento) {
    const ref = new Date(lead.ultimoMovimento).getTime();
    return Math.max(0, Math.floor((TODAY.getTime() - ref) / 86400000));
  }
  return lead.diasNoEstagio || 0;
}
// Alerta de lead parado: limite = 1,8× mediana histórica da etapa no funil
// (fallback: SLA da etapa enquanto não houver amostra). Ver pipeline-health.jsx.
function alerteParado(lead, stages, allLeads) {
  const h = leadHealth(lead, allLeads || [], stages);
  return h.parado ? { nivel: h.parado, dias: h.dias, health: h } : null;
}
// Atraso de entrega (previsaoEntrega já passou)
function entregaAtrasada(lead) {
  if (!lead.previsaoEntrega) return false;
  return new Date(lead.previsaoEntrega) < new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate());
}
function fmtEntrega(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  const mes = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"][d.getMonth()];
  return String(d.getDate()).padStart(2, "0") + "/" + mes;
}

// Selo de "parado há N dias", selo de inatividade e selos de pedido no ERP
function LeadCardBadges({ lead, stages }) {
  const store = useStore();
  const health = !lead.perdido ? leadHealth(lead, store.leads, stages) : null;
  // leadHealth() já decide "parado" na raiz (pipeline-health.jsx): no
  // fallback por SLA, sem ultimo_movimento_em não acusa. ATENÇÃO: isso NÃO
  // garante que Kanban e Gestão concordem — são duas fontes de etapas
  // diferentes (Kanban lê `store.etapasPorFunil`, do banco; `gestao.jsx`
  // lê `STAGES_BY_FUNNEL`, a constante de data.jsx). A migration 0029
  // alinhou o CONTEÚDO (mesmos SLAs, mesmas etapas em Lojistas), então hoje
  // os dois números batem — mas se um dos dois mudar sem o outro, voltam a
  // divergir sem aviso nenhum. Migrar os 8 arquivos restantes para
  // `store.etapasPorFunil` (achado 5 da revisão de 21/08) é o que fecha
  // essa brecha de vez; até lá, isto aqui é uma coincidência mantida à mão.
  const parado = health?.parado ? health : null;
  const inativo = health?.inativo && !parado ? health : null;
  const isProd = !!lead.pedidoNomus;
  const atraso = isProd && entregaAtrasada(lead);
  // Lead que o agente de IA lançou sem cliente: sem cliente cadastrado não há proposta (src/cliente-do-lead.js).
  const semCliente = lead.origem === 'ia' && !lead.clienteId && !lead.perdido;
  if (!parado && !inativo && !isProd && !semCliente) return null;
  return (
    <div className="lc-badges">
      {semCliente && (
        <span className="lc-sem-cliente" title="Lead de IA sem cliente: cadastre ou vincule um cliente na aba Vínculos para fazer proposta">
          <Icon name="users" size={9} /> Sem cliente
        </span>
      )}
      {parado && (
        <span className={`lc-stale ${parado.parado}`} title={`Parado há ${parado.dias} dias · limite ${parado.limite}d · ${phBaseLabel(parado)}`}>
          <Icon name="alert-triangle" size={9} /> Parado há {parado.dias}d
        </span>
      )}
      {inativo && (
        <span className="lc-inativo" title={`Nenhuma atividade registrada há ${inativo.semAtiv} dias`}>
          <Icon name="clock" size={9} /> Sem contato {inativo.semAtiv}d
        </span>
      )}
      {isProd && (
        <span className="lc-prodtag"><Icon name="package" size={9} /> Em Produção</span>
      )}
      {isProd && atraso && (
        <span className="lc-stale danger"><Icon name="alert-triangle" size={9} /> Atraso</span>
      )}
      {isProd && lead.pedidoNomus && (
        <span className="lc-cp mono">{lead.pedidoNomus}</span>
      )}
      {isProd && lead.previsaoEntrega && (
        <span className={`lc-entrega ${atraso ? "late" : ""}`}><Icon name="truck" size={9} /> Entrega: {fmtEntrega(lead.previsaoEntrega)}</span>
      )}
    </div>
  );
}

// O arrastar (HTML5 drag and drop) não funciona de forma confiável com o dedo, e o deslize horizontal do board
// disputa o gesto: no celular este botão é o único caminho de toque para trocar a etapa. Só aparece onde o arrastar
// aparece (`arrastavel`) e nunca em negócio perdido. A regra de CSS esconde o botão no computador com mouse.
// `stopPropagation`: o cartão inteiro abre o negócio ao toque.
function BotaoMover({ lead, arrastavel, onMover }) {
  if (!arrastavel || lead.perdido || !onMover) return null;
  return (
    <button
      type="button"
      className="btn sm ghost cartao-mover"
      onClick={(e) => { e.stopPropagation(); onMover(lead); }}
    >
      Mover
    </button>
  );
}

// Lista de etapas do board. A etapa atual fica desligada: mover para onde já está não faz nada.
function MoverEtapaModal({ lead, etapas, onEscolher, onClose }) {
  return (
    <ModalShell title="Mover negócio" icon="arrow-right" onClose={onClose} width={420} className="mover-etapa-modal">
      <div className="mover-etapa-empresa">{lead.empresa}</div>
      <div className="mover-etapa-lista">
        {etapas.map(s => {
          const atual = s.id === lead.stage;
          return (
            <button
              key={s.id}
              type="button"
              className={`mover-etapa-linha ${atual ? "atual" : ""}`}
              disabled={atual}
              aria-current={atual ? "true" : undefined}
              onClick={() => onEscolher(s.id)}
            >
              <span className={`stage-dot stage-${s.id}`} />
              <span className="mover-etapa-nome">{s.short || s.name}</span>
              {atual && <span className="mover-etapa-marca">Etapa atual</span>}
            </button>
          );
        })}
      </div>
    </ModalShell>
  );
}

// Card mode: "mini" (default — nome, valor, prazo, alerta)  vs  "detalhado" (mais informação)
function LeadCard({ lead, onOpen, onDragStart, isDragging, agentName, cardMode = "mini", cliente, especificador, stages, arrastavel = true, onMover }) {
  const tipo = lead.tipoCliente ? TIPO_CLIENTE_BY_ID[lead.tipoCliente] : null;
  const perdido = !!lead.perdido;

  if (cardMode === "mini") {
    return (
      <div
        className={`lead-card mini ${isDragging ? "dragging" : ""} ${perdido ? "perdido" : ""}`}
        draggable={!perdido && arrastavel}
        onDragStart={(e) => { if (!perdido && arrastavel) onDragStart(lead.id, e); }}
        onClick={() => onOpen(lead.id)}
        data-screen-label={`Lead Card / ${lead.empresa}`}
      >
        {perdido && <div className="perdido-tag"><Icon name="x" size={9} /> Perdido · {lead.motivoPerda}</div>}
        <LeadCardBadges lead={lead} stages={stages} />
        <div className="mini-row1">
          <div className="company">{lead.empresa}</div>
          {!perdido && lead.proximaDue && <Icon name="alert-triangle" size={12} style={{ color: "var(--color-danger)", flexShrink: 0 }} />}
        </div>
        <div className="mini-row2">
          <span className={`value mono ${lead.valor === 0 ? "zero" : ""}`}>{lead.valor > 0 ? brl(lead.valor) : "—"}</span>
          {lead.proximaAcao && lead.proximaAcao !== "—" && (
            <span className={`mini-due ${lead.proximaDue ? "due" : ""}`}>
              <Icon name="clock" size={10} /> {lead.proximaAcao.length > 22 ? lead.proximaAcao.slice(0, 22) + "…" : lead.proximaAcao}
            </span>
          )}
        </div>
        {tipo && (
          <div className="mini-tipo">
            <span className="dot" style={{ background: tipo.color }} />
            <span className="lbl">{tipo.short}</span>
            {especificador && <span className="esp" title={especificador.nome}>· {especificador.nome.split(" ").slice(0, 2).join(" ")}</span>}
            <HeatDot heat={lead.calor} />
          </div>
        )}
        <BotaoMover lead={lead} arrastavel={arrastavel} onMover={onMover} />
      </div>
    );
  }

  // Modo Detalhado
  return (
    <div
      className={`lead-card detail ${isDragging ? "dragging" : ""} ${perdido ? "perdido" : ""}`}
      draggable={!perdido && arrastavel}
      onDragStart={(e) => { if (!perdido && arrastavel) onDragStart(lead.id, e); }}
      onClick={() => onOpen(lead.id)}
      data-screen-label={`Lead Card / ${lead.empresa}`}
    >
      {perdido && <div className="perdido-tag"><Icon name="x" size={9} /> Perdido · {lead.motivoPerda}</div>}
      <LeadCardBadges lead={lead} stages={stages} />
      <div className="row1">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="company">{lead.empresa}</div>
          <div className="contact">
            <Icon name="user" size={11} strokeWidth={2} />
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.contato} · {lead.cargo}</span>
          </div>
        </div>
        <OriginPill origin={lead.origem} agentName={agentName} compact />
      </div>

      {cliente && (
        <div className="card-cli-line">
          <Icon name="users" size={11} style={{ color: "var(--color-ink-soft)" }} />
          <span>{cliente.nome}</span>
          {tipo && <TipoChip tipo={tipo} size="sm" />}
        </div>
      )}

      {especificador && (
        <div className="card-cli-line">
          <Icon name="compass" size={11} style={{ color: "var(--color-cool)" }} />
          <span>{especificador.nome}</span>
          {lead.parceriaRepasse && <span className="repasse-pip mono">{lead.pctRepasse}%</span>}
        </div>
      )}

      {lead.proximaAcao && lead.proximaAcao !== "—" && (
        <div className={`next-action ${lead.proximaDue ? "due" : ""}`}>
          <Icon name={lead.proximaDue ? "alert-triangle" : "clock"} size={12} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.proximaAcao}</span>
          {lead.proximaDue && <span className="when">vencida</span>}
        </div>
      )}

      {lead.tags && lead.tags.length > 0 && (
        <div className="tags-row">
          {lead.tags.includes("enterprise") && <span className="tag accent">Enterprise</span>}
          {lead.tags.includes("fit_alto") && <span className="tag accent">Fit alto</span>}
          {lead.tags.includes("hot_lead") && <span className="tag warn">Hot lead</span>}
          {lead.tags.includes("mid_market") && <span className="tag">Mid-market</span>}
        </div>
      )}

      <div className="row-bottom">
        <span className={`value mono ${lead.valor === 0 ? "zero" : ""}`}>{lead.valor > 0 ? brl(lead.valor) : "—"}</span>
        <HeatDot heat={lead.calor} />
      </div>
      <BotaoMover lead={lead} arrastavel={arrastavel} onMover={onMover} />
    </div>
  );
}

function LostReasonModal({ lead, onCancel, onConfirm }) {
  const [reason, setReason] = useStateKb(null);
  const [obs, setObs] = useStateKb("");
  const isOutro = reason === "outro";
  const ok = !!reason && (!isOutro || obs.trim().length >= 5);
  return (
    <div className="modal-back" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-head">
          <div style={{ width: 32, height: 32, background: "var(--color-danger-soft)", color: "var(--color-danger)", borderRadius: "var(--radius-sm)", display: "grid", placeItems: "center" }}>
            <Icon name="alert-triangle" size={16} />
          </div>
          <div className="title">Motivo da perda</div>
          <button className="icon-btn" onClick={onCancel}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 13, color: "var(--color-ink-soft)", marginBottom: 14 }}>
            Arquivando <strong style={{ color: "var(--color-ink)" }}>{lead?.empresa}</strong> como <strong style={{ color: "var(--color-danger)" }}>Perdido</strong>.
            Para fechar o ciclo e alimentar o BI, selecione o motivo <strong>e</strong> escreva uma breve observação.
          </div>
          <div className="reason-grid">
            {LOST_REASONS.map(r => (
              <button key={r.id} className={`reason-tile ${reason === r.id ? "active" : ""}`} onClick={() => setReason(r.id)}>
                <span className="ic"><Icon name={r.icon} size={16} /></span>
                {r.label}
              </button>
            ))}
          </div>
          <div className="field" style={{ marginTop: 14, marginBottom: 0 }}>
            <label>Observação {isOutro ? <span style={{ color: "var(--color-danger)" }}>*</span> : null} <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "var(--color-ink-faint)", marginLeft: 6 }}>· {isOutro ? "obrigatória — descreva o motivo" : "opcional, mas ajuda o BI"}</span></label>
            <textarea className="textarea" placeholder="Ex: cliente fechou com fornecedor B por R$ 20k a menos. Tentar tabela canal alternativa em Q3." value={obs} onChange={e => setObs(e.target.value)} />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn ghost" onClick={onCancel}>Cancelar</button>
          <button className="btn danger" disabled={!ok} onClick={() => onConfirm(LOST_REASONS.find(r => r.id === reason).label, obs)} style={{ opacity: ok ? 1 : 0.5 }}>
            <Icon name="x" size={14} />
            Confirmar perda
          </button>
        </div>
      </div>
    </div>
  );
}

// Card especial para leads na coluna Nova Entrada (enriquecidos por agentes)
function EntradaCard({ lead, onOpen, onDragStart, isDragging, onDescartar, arrastavel = true, podePerder = true, onMover }) {
  const store = useStore();
  const scoreColor = !lead.scoreFit ? 'var(--color-ink-faint)' : lead.scoreFit >= 80 ? 'var(--color-ok)' : lead.scoreFit >= 60 ? 'var(--color-warn)' : 'var(--color-ink-soft)';
  return (
    <div
      className={'lead-card entrada-card' + (isDragging ? ' dragging' : '')}
      draggable={arrastavel}
      onDragStart={(e) => { if (arrastavel) onDragStart(lead.id, e); }}
      onClick={() => onOpen(lead.id)}
      data-screen-label={'Entrada Card / ' + lead.empresa}
    >
      <div className="ec-header">
        <span className="ec-badge-ia"><Icon name="cpu" size={10} /> IA</span>
        {lead.scoreFit > 0 && (
          <span className="ec-score" style={{ color: scoreColor, fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700 }}>Fit {lead.scoreFit}</span>
        )}
      </div>
      <div className="ec-empresa">{lead.empresa}</div>
      {lead.segmento && lead.segmento !== '—' && (
        <div className="ec-segmento">{lead.segmento}</div>
      )}
      {lead.fonteCadeia && (
        <div className="ec-fonte"><Icon name="database" size={10} /> {lead.fonteCadeia}</div>
      )}
      <div className="ec-actions" onClick={e => e.stopPropagation()}>
        {arrastavel && (
          <button className="btn sm primary" onClick={() => store.moveLeadStage(lead.id, 'QUALIFICADO')}>
            <Icon name="check" size={11} /> Qualificar
          </button>
        )}
        <BotaoMover lead={lead} arrastavel={arrastavel} onMover={onMover} />
        {podePerder && (
          <button className="btn sm danger ghost" onClick={() => onDescartar(lead)}>
            <Icon name="x" size={11} /> Descartar
          </button>
        )}
      </div>
    </div>
  );
}

function KanbanColumn({ stage, leads, onCardOpen, onDrop, dragOverId, setDragOverId, onDragStart, draggingId, agentLookup, cardMode, clientesById, especificadoresById, onDescartar, stages, compact, podeMover = true, podePerder = true, escreveNoLead = () => true, onMover }) {
  const ativos = leads.filter(l => !l.perdido);
  const perdidos = leads.filter(l => l.perdido);
  const count = ativos.length;
  const sum = ativos.reduce((s, l) => s + (l.valor || 0), 0);
  const dragOver = dragOverId === stage.id;
  const cls = `column ${dragOver ? "drag-over" : ""} ${stage.id === "GANHO" ? "won" : ""}`;
  // Ordena: ativos primeiro, perdidos ao final da própria coluna
  const ordered = [...ativos, ...perdidos];

  // Coluna compacta (vazia): exibe só o rótulo vertical
  if (compact) {
    return (
      <div
        className={`column col-compact ${stage.id === "GANHO" ? "won" : ""} ${dragOver ? "drag-over" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragOverId(stage.id); }}
        onDragLeave={() => setDragOverId(null)}
        onDrop={(e) => { e.preventDefault(); setDragOverId(null); onDrop(stage.id); }}
        title={`${stage.name} · vazia${STAGE_GATES[stage.id] ? ` · critério de saída: ${STAGE_GATES[stage.id].criterio} (exige ${STAGE_GATES[stage.id].exige.toLowerCase()})` : ""}`}
      >
        <div className="col-compact-inner">
          <span className={`stage-dot stage-${stage.id}`} />
          <span className="col-compact-label">{stage.short || stage.name}</span>
          {STAGE_GATES[stage.id] && <Icon name="shield-check" size={10} className="col-compact-gate" />}
          <span className="col-compact-count">0</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cls}
      onDragOver={(e) => { e.preventDefault(); setDragOverId(stage.id); }}
      onDragLeave={() => setDragOverId(null)}
      onDrop={(e) => { e.preventDefault(); setDragOverId(null); onDrop(stage.id); }}
    >
      <div className="col-head">
        <div className="top">
          <span className={`stage-dot stage-${stage.id}`} />
          <span className="name">{stage.short || stage.name}</span>
          <span className="count">{count}</span>
          {perdidos.length > 0 && <span className="count lost" title={`${perdidos.length} perdido(s) nesta etapa`}>{perdidos.length} perd.</span>}
        </div>
        <div className="sum">
          {sum > 0 ? <><span>Soma</span> <strong>{brlCompact(sum)}</strong></> : <span style={{ color: "var(--color-ink-faint)" }}>Sem valores</span>}
        </div>
        {STAGE_GATES[stage.id] && (
          <div className="col-criterio" title={`Critério de saída · exige: ${STAGE_GATES[stage.id].exige}`}>
            <Icon name="shield-check" size={9} /> {STAGE_GATES[stage.id].criterio}
          </div>
        )}
      </div>
      <div className="col-body">
        {ordered.map(l => (
          l.stage === 'ENTRADA' ? (
            <EntradaCard
              key={l.id}
              lead={l}
              onOpen={onCardOpen}
              onDragStart={onDragStart}
              isDragging={draggingId === l.id}
              onDescartar={onDescartar}
              arrastavel={podeMover && escreveNoLead(l)}
              podePerder={podePerder && escreveNoLead(l)}
              onMover={onMover}
            />
          ) : (
            <LeadCard
              key={l.id}
              lead={l}
              onOpen={onCardOpen}
              onDragStart={onDragStart}
              isDragging={draggingId === l.id}
              agentName={agentLookup[l.agenteId]}
              cardMode={cardMode}
              cliente={l.clienteId ? clientesById[l.clienteId] : null}
              especificador={l.especificadorId ? especificadoresById[l.especificadorId] : null}
              stages={stages}
              arrastavel={podeMover && escreveNoLead(l)}
              onMover={onMover}
            />
          )
        ))}
        {ordered.length === 0 && <div className="empty" style={{ padding: "20px 8px", fontSize: 11.5 }}>Sem negócios neste estágio</div>}
      </div>
    </div>
  );
}

function FunnelSwitcher({ value, onChange }) {
  const store = useStore();
  return (
    <div className="funnel-switcher">
      {store.funis.map(f => (
        <button key={f.id} className={`fs-btn ${value === f.id ? "on" : ""}`} onClick={() => onChange(f.id)}>
          <div className="fs-icon" style={{ color: value === f.id ? f.accent : "var(--color-ink-soft)" }}><Icon name={f.icon} size={15} /></div>
          <div className="fs-lbl">
            <div className="nm">{f.name}</div>
            <div className="sub">{f.subtitle}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

function KanbanBoard({ leads, onCardOpen, toast, cardMode = "mini", funilId = "projetos" }) {
  const store = useStore();
  // O banco recusa mover e perder sem estas permissões (0088): sem elas o cartão nem arrasta.
  const permsKb = usePermissions('pipeline.move_stage', 'pipeline.mark_lost');
  const podeMover = permsKb['pipeline.move_stage'];
  const podePerder = permsKb['pipeline.mark_lost'];
  const escreveNoLead = useEscreveNoLead();
  const [draggingId, setDraggingId] = useStateKb(null);
  const [dragOverId, setDragOverId] = useStateKb(null);
  const [pendingLost, setPendingLost] = useStateKb(null);
  const [pendingGate, setPendingGate] = useStateKb(null);
  const [pendingMove, setPendingMove] = useStateKb(null);
  const [mobileColIdx, setMobileColIdx] = useStateKb(0);

  const stages = store.etapasPorFunil[funilId] || [];
  const papel = (store.me?.papel ?? 'admin').toLowerCase();
  const canSeeEntrada = ['admin', 'diretor', 'sdr', 'bdr'].includes(papel);
  // Perda é status arquivado (badge no card / filtro à parte), não coluna do fluxo
  // horizontal — por isso `isPerdido` sai da lista de colunas. Filtra pela flag que a
  // tradução da Etapa 2 já traz do banco, não pelo código, para não depender de qual
  // código a etapa de perda usa em cada funil.
  const visibleStages = (canSeeEntrada ? stages : stages.filter(s => s.id !== 'ENTRADA'))
    .filter(s => !s.isPerdido);
  const agentLookup = useMemoKb(() => Object.fromEntries(AGENTS.map(a => [a.id, a.name])), []);
  const clientesById = useMemoKb(() => Object.fromEntries(store.clientes.map(c => [c.id, c])), [store.clientes]);
  const especificadoresById = useMemoKb(() => Object.fromEntries(store.especificadores.map(e => [e.id, e])), [store.especificadores]);

  const byStage = useMemoKb(() => {
    const m = Object.fromEntries(visibleStages.map(s => [s.id, []]));
    for (const l of leads) {
      // Show only leads in the visible (horizontal) stages; PERDIDO is filtered out from kanban view
      if (m[l.stage]) m[l.stage].push(l);
    }
    return m;
  }, [leads, visibleStages]);

  const onDragStart = (id, e) => {
    setDraggingId(id);
    try { e.dataTransfer.setData("text/plain", id); e.dataTransfer.effectAllowed = "move"; } catch {}
  };
  const onDrop = (targetStage) => {
    const id = draggingId;
    setDraggingId(null);
    if (!id) return;
    moverPara(id, targetStage);
  };
  // Única regra de mudança de etapa: o arrastar (onDrop) e o botão "Mover" do cartão passam por aqui. Duas cópias
  // deixariam o toque pular o critério de saída que o arrastar respeita.
  const moverPara = (id, targetStage) => {
    const lead = leads.find(l => l.id === id);
    if (!lead || lead.stage === targetStage) return;
    const idx = (st) => stages.findIndex(s => s.id === st);
    const regressao = idx(targetStage) >= 0 && idx(lead.stage) >= 0 && idx(targetStage) < idx(lead.stage);
    // Regressão é legítima (o critério de saída deixou de ser verdade) e não passa pelo gate.
    if (!regressao) {
      const { ok, gate } = gateStatus(lead, targetStage, { propostas: store.getPropostasByLead(lead.id) });
      if (!ok && gate) { setPendingGate({ lead, targetStage, gate }); return; }
    }
    // O toast (sucesso ou falha) mora dentro de moveLeadStage: só ele sabe se a
    // gravação no banco realmente aconteceu. Emitir "sucesso" aqui, sem esperar,
    // era o defeito — o card fica na coluna de origem até a escrita ser confirmada
    // (leads vem do estado do store, não é movido de forma otimista), mas o toast
    // desta função anunciava a mudança mesmo quando a RLS recusava (achado 2 da
    // revisão final).
    store.moveLeadStage(id, targetStage);
  };

  const confirmGate = (justificativa) => {
    const { lead, targetStage } = pendingGate;
    setPendingGate(null);
    store.moveLeadStage(lead.id, targetStage, { justificativa });
  };

  const confirmLost = (reasonLabel, obs) => {
    store.markLeadLost(pendingLost.lead.id, reasonLabel, obs);
    setPendingLost(null);
  };

  const wrapRef = useRef(null);
  useEffect(() => {
    const el = wrapRef.current?.querySelector(".kanban");
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth;
      const idx = Math.round(el.scrollLeft / w);
      setMobileColIdx(Math.min(visibleStages.length - 1, Math.max(0, idx)));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [visibleStages.length]);

  return (
    <div className="kanban-wrap" ref={wrapRef}>
      <div className="kanban" data-card-mode={cardMode}>
        {visibleStages.map(s => {
          const stageleads = byStage[s.id] || [];
          const stageAtivos = stageleads.filter(l => !l.perdido);
          // Colunas terminais ou vazias sem leads ativos → modo compacto
          const isCompact = stageAtivos.length === 0
            && s.id !== 'ENTRADA'
            && s.id !== 'OUT_NOVA'
            && s.id !== 'QUALIFICADO';
          return (
            <KanbanColumn
              key={s.id}
              stage={s}
              leads={stageleads}
              onCardOpen={onCardOpen}
              onDrop={onDrop}
              dragOverId={dragOverId}
              setDragOverId={setDragOverId}
              onDragStart={onDragStart}
              draggingId={draggingId}
              agentLookup={agentLookup}
              cardMode={cardMode}
              clientesById={clientesById}
              especificadoresById={especificadoresById}
              onDescartar={(lead) => setPendingLost({ lead })}
              stages={stages}
              compact={isCompact}
              podeMover={podeMover}
              podePerder={podePerder}
              escreveNoLead={escreveNoLead}
              onMover={(lead) => setPendingMove({ lead })}
            />
          );
        })}
      </div>
      <div className="col-indicator">
        {visibleStages.map((s, i) => <span key={s.id} className={'pip ' + (i === mobileColIdx ? 'on' : '')} />)}
      </div>
      {pendingMove && (
        <MoverEtapaModal
          lead={pendingMove.lead}
          etapas={visibleStages}
          onClose={() => setPendingMove(null)}
          onEscolher={(etapa) => { const { lead } = pendingMove; setPendingMove(null); moverPara(lead.id, etapa); }}
        />
      )}
      {pendingGate && (
        <GateModal
          lead={pendingGate.lead}
          gate={pendingGate.gate}
          targetStageName={STAGE_NAMES[pendingGate.targetStage]}
          onCancel={() => setPendingGate(null)}
          onConfirm={confirmGate}
        />
      )}
      {pendingLost && (
        <LostReasonModal
          lead={pendingLost.lead}
          onCancel={() => setPendingLost(null)}
          onConfirm={confirmLost}
        />
      )}
    </div>
  );
}

export { KanbanBoard, LeadCard, LostReasonModal, FunnelSwitcher };
